import type { LucideIcon } from 'lucide-react';
import type { SiteType } from '@/config/site';
import type { NavContent } from '@/hooks/useNavBadges';
import {
  History,
  ListChecks,
  LayoutDashboard, Package, FolderTree, ClipboardList, Users, Boxes,
  FileText, PenSquare, FolderOpen, Home, Tag, Star, Truck, Mail,
  Settings, ShoppingCart, TrendingUp, Gift, Timer, Award, Share2,
  Heart, Zap, Palette, Send, Clock, RotateCcw, BarChart3, Cog, Store,
  Bot, MessageSquare, ListTodo, Settings2, Bell, CheckSquare, Search,
  BookOpen, GraduationCap, CreditCard, BadgeDollarSign, Globe,
  Megaphone, DollarSign, Key, Building2, Blocks, PiggyBank, Handshake, CalendarDays,
  Ticket, KeyRound, BedDouble, Percent, RefreshCw, ShieldCheck, Activity, Receipt, LogIn,
  FlaskConical,
  Brush,
  Paintbrush,
  Link2,
  Tv,
  Images,
  Compass,
} from 'lucide-react';

export type BadgeColor = 'red' | 'amber';

export interface AdminNavItem {
  label: string;
  href: string;
  icon: LucideIcon;
  featureFlag?: string;
  /**
   * Site types this item is also relevant to, independent of `featureFlag`.
   * Visibility is OR: the item shows when its featureFlag is enabled OR the
   * current SITE_TYPE is listed here. Lets subscription-billing pages reach
   * saas/app tenants without the course-centric `course_subscriptions` flag.
   */
  siteTypes?: SiteType[];
  badgeKey?: string;
  badgeColor?: BadgeColor;
  superAdmin?: boolean;
  /**
   * Show only to the `designer` role. For orientation pages that would be
   * clutter for an admin, who has the dashboard and the whole nav already.
   */
  designerOnly?: boolean;
  external?: boolean;
  /**
   * Hide this item until the tenant actually has content behind it. Keyed
   * against `NavContent` from `hooks/useNavBadges`. The route stays mounted —
   * this only stops the sidebar advertising a page that is empty on virtually
   * every tenant.
   */
  hideWhenEmptyKey?: keyof NavContent;
}

/**
 * A collapsible sub-group of items rendered under a section header. Lets the
 * big sections (Advertising, Lodging, Fundraising, …) break a long flat list
 * into a few labeled clusters. Gating fields mirror AdminNavItem and reuse
 * `navItemVisible`.
 */
export interface AdminNavGroup {
  key: string;
  label: string;
  items: AdminNavItem[];
  featureFlag?: string;
  siteTypes?: SiteType[];
  superAdmin?: boolean;
}

/**
 * Whether a nav item/group/section is visible for the current site type +
 * feature flags. OR semantics: visible when its `featureFlag` is enabled OR its
 * `siteTypes` includes the current type. Entries with neither constraint are
 * always visible. (super-admin gating is applied separately by the layout.)
 */
export function navItemVisible(
  item: Pick<AdminNavItem, 'featureFlag' | 'siteTypes'>,
  siteType: SiteType,
  isEnabled: (key: string) => boolean,
): boolean {
  if (!item.featureFlag && !item.siteTypes) return true;
  const flagOk = item.featureFlag ? isEnabled(item.featureFlag) : false;
  const typeOk = item.siteTypes ? item.siteTypes.includes(siteType) : false;
  return flagOk || typeOk;
}

export interface AdminNavSection {
  key: string;
  label: string;
  featureFlag?: string;
  siteTypes?: SiteType[];
  superAdmin?: boolean;
  /** Directly-rendered items (shown above any groups). Always an array. */
  items: AdminNavItem[];
  /** Optional collapsible sub-groups rendered below the ungrouped items. */
  groups?: AdminNavGroup[];
  collapsible?: boolean;
  /**
   * Site types for which this is the *primary* domain section. When the current
   * SITE_TYPE matches, the section floats to the top (just under Dashboard) via
   * `getOrderedSections`, so e.g. a lodging tenant sees LODGING first instead of
   * buried below the cross-cutting sections.
   */
  primaryFor?: SiteType[];
}

export const adminNavSections: AdminNavSection[] = [
  {
    key: 'dashboard',
    label: 'DASHBOARD',
    collapsible: false,
    items: [
      { label: 'Dashboard', href: '/admin', icon: LayoutDashboard },
    ],
  },
  {
    key: 'catalog',
    label: 'CATALOG',
    featureFlag: 'ecommerce',
    primaryFor: ['ecommerce'],
    items: [
      { label: 'Products', href: '/admin/products', icon: Package },
      { label: 'Categories', href: '/admin/categories', icon: FolderTree },
      { label: 'Inventory', href: '/admin/inventory', icon: Boxes, badgeKey: 'low_stock', badgeColor: 'amber' },
    ],
  },
  {
    key: 'orders',
    label: 'ORDERS & SHIPPING',
    featureFlag: 'ecommerce',
    primaryFor: ['ecommerce'],
    items: [
      { label: 'Orders', href: '/admin/orders', icon: ClipboardList, badgeKey: 'pending_orders', badgeColor: 'red' },
      { label: 'Fulfillment', href: '/admin/fulfillment', icon: Package },
      { label: 'Returns', href: '/admin/returns', icon: RotateCcw, badgeKey: 'pending_returns', badgeColor: 'red' },
      { label: 'Carts', href: '/admin/carts', icon: ShoppingCart },
      { label: 'Shipping Config', href: '/admin/shipping', icon: Truck },
      { label: 'Fulfillment Rules', href: '/admin/fulfillment-rules', icon: Cog },
      { label: 'Shipping Analytics', href: '/admin/fulfillment-dashboard', icon: BarChart3 },
    ],
  },
  {
    key: 'growth',
    label: 'GROWTH',
    featureFlag: 'ecommerce',
    primaryFor: ['ecommerce'],
    items: [
      { label: 'Upsell Dashboard', href: '/admin/upsell', icon: LayoutDashboard },
      { label: 'Associations', href: '/admin/upsell/associations', icon: Heart },
      { label: 'Volume Discounts', href: '/admin/upsell/volume-discounts', icon: TrendingUp, featureFlag: 'volume_discounts' },
      { label: 'Bundles', href: '/admin/upsell/bundles', icon: Package, featureFlag: 'product_bundles' },
      { label: 'Countdown Timers', href: '/admin/upsell/timers', icon: Timer, featureFlag: 'countdown_timers' },
      { label: 'Email Automation', href: '/admin/upsell/email-automation', icon: Mail },
      { label: 'Loyalty Program', href: '/admin/upsell/loyalty', icon: Award, featureFlag: 'loyalty_program' },
      { label: 'Gift Cards', href: '/admin/upsell/gift-cards', icon: Gift, featureFlag: 'gift_cards' },
    ],
  },
  {
    key: 'lodging',
    label: 'LODGING',
    featureFlag: 'properties',
    primaryFor: ['lodging'],
    items: [
      { label: 'KPI Dashboard', href: '/admin/lodging', icon: LayoutDashboard },
    ],
    groups: [
      {
        key: 'listings',
        label: 'Listings',
        items: [
          { label: 'Properties', href: '/admin/properties', icon: BedDouble },
          { label: 'Calendar', href: '/admin/properties-calendar', icon: CalendarDays },
          { label: 'Reservations', href: '/admin/reservations', icon: KeyRound, featureFlag: 'bookings' },
          { label: 'Pricing Rules', href: '/admin/pricing-rules', icon: Percent },
        ],
      },
      {
        key: 'guest-ops',
        label: 'Guest Ops',
        items: [
          { label: 'Messages', href: '/admin/messages', icon: MessageSquare },
          { label: 'Turnovers', href: '/admin/turnovers', icon: Brush },
          { label: 'Cleaners', href: '/admin/cleaners', icon: Users },
          { label: 'Reviews', href: '/admin/lodging-reviews', icon: Star, featureFlag: 'reviews' },
          { label: 'Welcome Screens', href: '/admin/welcome-screens', icon: Tv, featureFlag: 'welcome_screens' },
        ],
      },
      {
        key: 'integrations',
        label: 'Integrations',
        items: [
          { label: 'Channel Sync', href: '/admin/channel-sync', icon: RefreshCw },
          { label: 'OwnerRez', href: '/admin/ownerrez', icon: Link2, featureFlag: 'ownerrez_sync' },
        ],
      },
    ],
  },
  {
    key: 'events',
    label: 'EVENTS',
    featureFlag: 'events',
    primaryFor: ['museum'],
    items: [
      { label: 'All Events', href: '/admin/events', icon: Ticket },
    ],
  },
  {
    key: 'courses',
    label: 'COURSES',
    featureFlag: 'courses',
    primaryFor: ['saas'],
    items: [
      { label: 'Courses', href: '/admin/courses', icon: BookOpen },
      { label: 'Enrollments', href: '/admin/enrollments', icon: GraduationCap },
      // Subscription Plans moved to CUSTOMERS — this section is hidden whole
      // when the `courses` flag is off, which locked subscription-only tenants
      // out of their own plan editor.
      { label: 'Founders Pricing', href: '/admin/founders-pricing', icon: BadgeDollarSign, featureFlag: 'founders_pricing' },
    ],
  },
  {
    key: 'responsible_play',
    label: 'RESPONSIBLE PLAY',
    siteTypes: ['social_casino'],
    primaryFor: ['social_casino'],
    items: [
      { label: 'Self-Exclusions', href: '/admin/social-casino/self-exclusions', icon: ShieldCheck },
    ],
  },
  {
    key: 'marketplace',
    label: 'MARKETPLACE',
    featureFlag: 'marketplace_channels',
    items: [
      { label: 'Overview', href: '/admin/marketplace', icon: Store },
      { label: 'Channels', href: '/admin/marketplace/channels', icon: ShoppingCart },
      { label: 'Listings', href: '/admin/marketplace/listings', icon: Package },
      { label: 'Analytics', href: '/admin/marketplace/analytics', icon: BarChart3 },
    ],
  },
  {
    key: 'dropshipping',
    label: 'DROPSHIPPING',
    featureFlag: 'dropshipping',
    items: [
      { label: 'Suppliers', href: '/admin/suppliers', icon: Truck },
      { label: 'Supplier Orders', href: '/admin/supplier-orders', icon: ClipboardList },
      { label: 'Prospects', href: '/admin/agent/prospects', icon: Search },
    ],
  },
  {
    key: 'fundraising',
    label: 'FUNDRAISING',
    featureFlag: 'fundraising',
    items: [
      { label: 'Penny Dashboard', href: '/admin/fundraising', icon: PiggyBank },
      { label: 'Chat with Penny', href: '/admin/fundraising/chat', icon: MessageSquare },
    ],
    groups: [
      {
        key: 'pipeline',
        label: 'Pipeline',
        items: [
          { label: 'Funders', href: '/admin/fundraising/funders', icon: Search },
          { label: 'Applications', href: '/admin/fundraising/applications', icon: ClipboardList },
        ],
      },
      {
        key: 'supporters',
        label: 'Supporters',
        items: [
          { label: 'Donors', href: '/admin/fundraising/donors', icon: Heart },
          { label: 'Sponsors', href: '/admin/fundraising/sponsors', icon: Handshake },
          { label: 'Members', href: '/admin/fundraising/members', icon: Users },
          { label: 'Membership Tiers', href: '/admin/fundraising/membership-tiers', icon: BadgeDollarSign, featureFlag: 'memberships' },
        ],
      },
      {
        key: 'organization',
        label: 'Organization',
        items: [
          { label: 'Org Profile', href: '/admin/fundraising/org-profile', icon: Building2 },
          { label: 'Calendar', href: '/admin/fundraising/calendar', icon: CalendarDays },
          { label: 'Penny Config', href: '/admin/fundraising/config', icon: Settings2 },
        ],
      },
    ],
  },
  {
    key: 'customers',
    label: 'CUSTOMERS',
    items: [
      { label: 'Customers', href: '/admin/customers', icon: Users },
      // Recurring plans. This lived under COURSES, whose section-level
      // `featureFlag: 'courses'` hid it entirely from a SaaS tenant that
      // doesn't sell courses — so a product like Vigil, whose whole business
      // is subscriptions, had no UI to define one. The item keeps its own
      // OR-gate; only its home moved.
      // (Subscription Promos already lives under MARKETING → Promotions.)
      {
        label: 'Subscription Plans',
        href: '/admin/subscription-plans',
        icon: CreditCard,
        featureFlag: 'course_subscriptions',
        siteTypes: ['saas'],
      },
    ],
  },
  {
    key: 'content',
    label: 'CONTENT',
    items: [
      { label: 'Start here', href: '/admin/design-start', icon: Compass, designerOnly: true },
      { label: 'Pages', href: '/admin/pages', icon: FileText },
      { label: 'Homepage Builder', href: '/admin/homepage', icon: Home },
      { label: 'Media Library', href: '/admin/media', icon: Images },
    ],
    groups: [
      {
        key: 'blog',
        label: 'Blog',
        items: [
          { label: 'Blog Posts', href: '/admin/blog', icon: PenSquare },
          { label: 'Blog Agent', href: '/admin/blog/agent', icon: Bot },
          { label: 'Blog Categories', href: '/admin/blog/categories', icon: FolderOpen },
        ],
      },
    ],
  },
  {
    key: 'marketing',
    label: 'MARKETING',
    items: [],
    groups: [
      {
        key: 'promotions',
        label: 'Promotions',
        items: [
          { label: 'Coupons & Discounts', href: '/admin/coupons', icon: Tag },
          // Subscription promo codes (Stripe coupons for plan subs). Ungated like
          // Coupons so it stays reachable on subscription/app tenants (whose
          // billing isn't behind the ecommerce flag).
          { label: 'Subscription Promos', href: '/admin/subscription-promos', icon: Ticket },
          // Referrals also serve subscription sites, so it keeps its own
          // referral_program flag rather than living under ecommerce-gated GROWTH.
          { label: 'Referral Program', href: '/admin/upsell/referrals', icon: Share2, featureFlag: 'referral_program' },
        ],
      },
      {
        key: 'email',
        label: 'Email & Newsletter',
        items: [
          { label: 'Email Popup', href: '/admin/email-popup', icon: Mail },
          { label: 'Subscribers', href: '/admin/newsletter/subscribers', icon: Users, featureFlag: 'newsletter' },
          { label: 'Campaigns', href: '/admin/newsletter/campaigns', icon: Send, featureFlag: 'newsletter' },
          { label: 'Schedules', href: '/admin/newsletter/schedules', icon: Clock, featureFlag: 'newsletter' },
        ],
      },
    ],
  },
  {
    key: 'agent',
    label: 'STORE AGENT',
    featureFlag: 'ai_features',
    items: [
      { label: 'Dashboard', href: '/admin/agent', icon: Bot },
      { label: 'Chat', href: '/admin/agent/chat', icon: MessageSquare },
      { label: 'Tasks', href: '/admin/agent/tasks', icon: ListTodo },
      { label: 'Schedules', href: '/admin/agent/schedules', icon: Clock },
      { label: 'Tool Stats', href: '/admin/agent/tool-stats', icon: BarChart3 },
      { label: 'Notifications', href: '/admin/agent/notifications', icon: Bell },
      { label: 'Approvals', href: '/admin/agent/approvals', icon: CheckSquare },
      { label: 'Config', href: '/admin/agent/config', icon: Settings2 },
    ],
  },
  {
    key: 'advertising',
    label: 'ADVERTISING',
    featureFlag: 'advertising',
    items: [],
    groups: [
      {
        key: 'addie',
        label: 'Addie',
        items: [
          { label: 'Addie Dashboard', href: '/admin/advertising', icon: Bot },
          { label: 'Addie Chat', href: '/admin/advertising/chat', icon: MessageSquare },
          // Lodging-only: gated to lodging tenants via siteTypes (the group's
          // section `advertising` flag still applies, so it shows only when
          // advertising is on AND the site is lodging).
          { label: 'Addie Review', href: '/admin/advertising/review', icon: CheckSquare, siteTypes: ['lodging'] },
          { label: 'Addie Tasks', href: '/admin/advertising/tasks', icon: ListTodo },
          { label: 'Addie Schedules', href: '/admin/advertising/schedules', icon: Clock },
          { label: 'Addie Config', href: '/admin/advertising/config', icon: Settings2 },
          { label: 'Addie Simulator', href: '/admin/advertising/simulator', icon: FlaskConical, superAdmin: true },
        ],
      },
      {
        key: 'campaigns',
        label: 'Campaigns',
        items: [
          { label: 'Campaigns', href: '/admin/advertising/campaigns', icon: Megaphone },
          { label: 'Platforms', href: '/admin/advertising/platforms', icon: Globe },
          { label: 'Budget Manager', href: '/admin/advertising/budgets', icon: DollarSign },
          // These four are empty on effectively every tenant, so they only
          // appear once there is something behind them.
          { label: 'Audiences', href: '/admin/advertising/audiences', icon: Users, hideWhenEmptyKey: 'ad_audiences' },
          { label: 'Funnels', href: '/admin/advertising/funnels', icon: Store, hideWhenEmptyKey: 'ad_funnels' },
        ],
      },
      {
        key: 'creative',
        label: 'Creative',
        items: [
          { label: 'Assets', href: '/admin/advertising/assets', icon: Palette },
          { label: 'Brand Cast', href: '/admin/advertising/cast', icon: Star, hideWhenEmptyKey: 'ad_cast' },
          { label: 'Video Studio', href: '/admin/advertising/videos', icon: Cog, hideWhenEmptyKey: 'ad_videos' },
          { label: 'Commercial Studio', href: '/admin/advertising/commercials', icon: Cog },
        ],
      },
      {
        key: 'insights',
        label: 'Insights',
        items: [
          { label: 'Analytics', href: '/admin/advertising/analytics', icon: BarChart3 },
          // The single canonical Local Events entry (deduped — was also listed
          // under LODGING). Lodging-only via siteTypes.
          { label: 'Local Events', href: '/admin/advertising/local-events', icon: CalendarDays, siteTypes: ['lodging'] },
        ],
      },
    ],
  },
  {
    key: 'analytics',
    label: 'REVIEWS & REPORTS',
    items: [
      { label: 'Reviews', href: '/admin/reviews', icon: Star, featureFlag: 'reviews' },
      { label: 'Search Analytics', href: '/admin/search-analytics', icon: BarChart3 },
      { label: 'Review Analytics', href: '/admin/reviews/analytics', icon: BarChart3, featureFlag: 'reviews' },
    ],
  },
  {
    key: 'apps',
    label: 'APPS',
    items: [
      { label: 'Apps', href: '/admin/apps', icon: Blocks },
      { label: 'Integrations', href: '/admin/integrations', icon: Share2, featureFlag: 'integrations' },
      { label: 'Customs', href: '/admin/customs', icon: Receipt, featureFlag: 'customs' },
      // Same destination as PLATFORM > Ownersbox, which is superAdmin-only and
      // therefore invisible to a tenant admin. This is the per-site copy: it
      // appears only where the `ownersbox` flag is on, i.e. for tenants who
      // actually have an Ownersbox account. Ungated it would send every tenant
      // to a dashboard whose sign-in is invite-only and fails closed — a link
      // to a refusal. A super admin on a flag-enabled site sees both entries,
      // which is correct: one is platform tooling, the other is this site's
      // enabled integration.
      { label: 'Ownersbox', href: 'https://ownersbox.rumio.world', icon: Activity, external: true, featureFlag: 'ownersbox' },
    ],
  },
  {
    key: 'platform',
    label: 'PLATFORM',
    superAdmin: true,
    items: [
      { label: 'New Site Wizard', href: '/admin/site-wizard', icon: Globe },
      { label: 'Move to Custom Domain', href: '/admin/promote-domain', icon: Globe },
      { label: 'Store Migrations', href: '/admin/platform/store-migrations', icon: Truck },
      { label: 'Platform Settings', href: '/admin/platform/settings', icon: Settings2 },
      { label: 'Platform Plans', href: '/admin/platform/plans', icon: BadgeDollarSign },
      { label: 'Promo Codes', href: '/admin/platform/promos', icon: Ticket },
      { label: 'Workspaces', href: '/admin/platform/workspaces', icon: CreditCard },
      { label: 'Dippy Fleet', href: '/admin/platform/agent-fleet', icon: Bot },
      { label: 'Ownersbox', href: 'https://ownersbox.rumio.world', icon: Activity, external: true },
    ],
  },
  {
    key: 'settings',
    label: 'SETTINGS',
    items: [
      { label: 'General', href: '/admin/settings', icon: Settings },
      { label: 'Business Profile', href: '/admin/agent/business-profile', icon: Building2 },
      { label: 'Theme', href: '/admin/settings/theme', icon: Palette },
      { label: 'Design Sandbox', href: '/admin/settings/sandbox', icon: FlaskConical },
      { label: 'Design History', href: '/admin/settings/design-history', icon: History },
      { label: 'Design Checklist', href: '/admin/settings/design-checklist', icon: ListChecks },
      { label: 'Branding Studio', href: '/admin/settings/branding', icon: Paintbrush, featureFlag: 'branding_studio' },
      { label: 'Email', href: '/admin/settings/email', icon: Mail },
      { label: 'Payments', href: '/admin/settings/payments', icon: CreditCard },
      { label: 'Security', href: '/admin/settings/security', icon: ShieldCheck },
      { label: 'Social Login', href: '/admin/settings/social-login', icon: LogIn },
      { label: 'Credentials', href: '/admin/settings/credentials', icon: Key },
      { label: 'Feature Toggles', href: '/admin/feature-toggles', icon: Zap },
    ],
  },
];

/**
 * All items in a section, flattening ungrouped `items` and every group's items
 * into one list. Used for active-route detection across the nested structure.
 */
export function flattenSectionItems(section: AdminNavSection): AdminNavItem[] {
  return [
    ...section.items,
    ...(section.groups ?? []).flatMap((g) => g.items),
  ];
}

/**
 * Collect all nav hrefs for active-route detection. External items hold
 * absolute URLs and can never match an in-app pathname, so they're skipped.
 */
const allNavHrefs = adminNavSections.flatMap((s) =>
  flattenSectionItems(s).filter((i) => !i.external).map((i) => i.href)
);

/**
 * Determine if a nav item is active given the current pathname.
 * Handles exact match for /admin and prefix matching for deeper routes,
 * while avoiding false positives when one href is a prefix of another.
 */
export function isRouteActive(pathname: string, href: string): boolean {
  if (pathname === href) return true;
  if (href === '/admin') return false;
  if (!pathname.startsWith(href + '/')) return false;
  // Ensure no other nav href is a more specific prefix match
  return !allNavHrefs.some(
    (h) => h !== href && h.startsWith(href + '/') && pathname.startsWith(h)
  );
}

/**
 * Find the section key that contains the currently active route (searching
 * ungrouped items and every sub-group).
 */
export function getActiveSectionKey(pathname: string): string | null {
  for (const section of adminNavSections) {
    if (flattenSectionItems(section).some((item) => !item.external && isRouteActive(pathname, item.href))) {
      return section.key;
    }
  }
  return null;
}

/**
 * Find the "sectionKey:groupKey" of the sub-group that contains the currently
 * active route, or null when the active route is ungrouped / unknown. Used to
 * auto-expand the sub-group the admin lands in.
 */
export function getActiveGroupKey(pathname: string): string | null {
  for (const section of adminNavSections) {
    for (const group of section.groups ?? []) {
      if (group.items.some((item) => !item.external && isRouteActive(pathname, item.href))) {
        return `${section.key}:${group.key}`;
      }
    }
  }
  return null;
}

/**
 * Order sections for display so the tenant's primary domain floats to the top.
 * Dashboard stays first; sections whose `primaryFor` includes the current site
 * type come next (in their original relative order); everything else follows in
 * its original order. Stable — the index tiebreaker preserves authored order.
 */
export function getOrderedSections(
  sections: AdminNavSection[],
  siteType: SiteType,
): AdminNavSection[] {
  const rank = (s: AdminNavSection): number => {
    if (s.key === 'dashboard') return 0;
    if (s.primaryFor?.includes(siteType)) return 1;
    return 2;
  };
  return sections
    .map((s, i) => ({ s, i }))
    .sort((a, b) => rank(a.s) - rank(b.s) || a.i - b.i)
    .map(({ s }) => s);
}
